// Photohawk environment resolution + override, so we can point the whole app at
// STAGING from any host (a preview deploy, localhost) for end-to-end testing.
//
// Precedence:
//   1. `?phEnv=staging|prod` query param (persisted to localStorage)
//   2. localStorage `photohawk_env`
//   3. host detection (a real *.photohawk.com host → prod)
//   4. default: staging (this is the staging deployment branch)
//
// Switching persists + reloads so the module-level API endpoint constants and
// Cognito config are recomputed for the chosen environment.

export type PhotohawkEnv = "staging" | "prod";

const STORAGE_KEY = "photohawk_env";
// Mirror the override into a cookie so the SSR server can see it per-request
// (localStorage is browser-only). When set, server.mjs serves the CSR shell so
// the client renders entirely against the overridden env — no staging SSR data
// / redirects leaking into a prod-backed test session. Same value space.
const ENV_COOKIE = "ph_env";
const writeEnvCookie = (env: PhotohawkEnv | null): void => {
  if (typeof document === "undefined") return;
  document.cookie = env
    ? `${ENV_COOKIE}=${env}; path=/; max-age=604800; samesite=lax`
    : `${ENV_COOKIE}=; path=/; max-age=0; samesite=lax`;
};

const hostIsProd = (): boolean => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  return host === "photohawk.com" || host.endsWith(".photohawk.com");
};

// Read the query param once on load and persist it, so it survives navigation.
const readQueryOverride = (): PhotohawkEnv | null => {
  if (typeof window === "undefined") return null;
  try {
    const v = new URLSearchParams(window.location.search).get("phEnv");
    if (v === "staging" || v === "prod") {
      localStorage.setItem(STORAGE_KEY, v);
      writeEnvCookie(v);
      return v;
    }
  } catch {
    // ignore
  }
  return null;
};

let cached: PhotohawkEnv | null = null;

export const getPhotohawkEnv = (): PhotohawkEnv => {
  if (cached) return cached;
  const fromQuery = readQueryOverride();
  if (fromQuery) {
    cached = fromQuery;
    return cached;
  }
  // Server-side (SSR): no window, so honour a container-pinned env var. Lets the
  // prod SSR server target the prod API even though host detection needs window.
  if (typeof window === "undefined") {
    try {
      const fromEnv =
        typeof process !== "undefined" ? process.env?.PHOTOHAWK_ENV : undefined;
      if (fromEnv === "staging" || fromEnv === "prod") {
        cached = fromEnv;
        return cached;
      }
    } catch {
      /* ignore */
    }
  }
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "staging" || stored === "prod") {
      cached = stored;
      return cached;
    }
    // The SSR server injects its pinned deployment env (PHOTOHAWK_ENV) as
    // `window.__PH_ENV__`. This is the ground truth for which backend this
    // deployment targets — host detection can't tell (a custom domain like
    // search.mysportphotos.co.uk is a PROD host but isn't *.photohawk.com, so
    // hostIsProd() would wrongly fall through to staging). Explicit ?phEnv /
    // localStorage overrides above still win for testers.
    const injected = (window as unknown as { __PH_ENV__?: string }).__PH_ENV__;
    if (injected === "staging" || injected === "prod") {
      cached = injected;
      return cached;
    }
  }
  // STAGING deployment branch: default to the staging backend + Cognito on any
  // non-prod host (e.g. *.pages.dev). A real prod host stays prod, and the
  // ?phEnv / localStorage overrides above still win.
  cached = hostIsProd() ? "prod" : "staging";
  return cached;
};

export const isStaging = (): boolean => getPhotohawkEnv() === "staging";

/** Is the environment explicitly overridden (vs. inferred from host)? */
export const hasEnvOverride = (): boolean => {
  if (typeof window === "undefined") return false;
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "staging" || stored === "prod";
};

/** Persist a new environment and reload so endpoints/Cognito are recomputed. */
export const setPhotohawkEnv = (env: PhotohawkEnv) => {
  localStorage.setItem(STORAGE_KEY, env);
  writeEnvCookie(env);
  cached = env;
  if (typeof window !== "undefined") window.location.reload();
};

export const clearEnvOverride = () => {
  localStorage.removeItem(STORAGE_KEY);
  writeEnvCookie(null);
  cached = null;
  if (typeof window !== "undefined") window.location.reload();
};
